// eslint-disable-next-line
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import './Auth.css';
import { useView } from '../../../context/ViewContext';
import { handleAuthSuccess } from '../../../utils/auth';
import {
  INDUSTRY_CATEGORY_OPTIONS,
  OTHER_INDUSTRY_CATEGORY,
} from '../../../utils/industry';
import ProfessionalPaymentSection from './ProfessionalPaymentSection';
import Footer from '../../Footer';
import PasswordField from './PasswordField';
import {
  clearStoredSignupMode,
  getBusinessInviteTokenFromNext,
  getSignupMode,
  getSignupModeFromSearch,
  isSignupAccountMode,
} from '../../../utils/signupMode';
import { PRIVACY_VERSION, TERMS_VERSION } from '../../../constants/legalVersions';
import { getSupportedBrowserTimeZone } from '../../../utils/timeZones';

// How long "Send it again" waits after an activation email goes out. Each
// resend replaces the link in the earlier email, so an instant resend would
// only leave the first email holding a dead link.
const RESEND_COOLDOWN_MS = 60 * 1000;

const ProfessionalActivationNotice = ({
  email,
  emailSent,
  message,
  onRetry,
  isRetrying,
  error,
  awaitingEmailConfirmation,
  resend,
  resendSecondsLeft,
  onResend,
  onUseDifferentEmail,
}) => {
  const headingRef = useRef(null);

  // This card replaces a long form, so on a phone the page can be left
  // scrolled past it. Focus brings it into view and has it announced.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  if (!awaitingEmailConfirmation) {
    return (
      <div className="auth-card activation-notice">
        <h3>Check your email</h3>
        <p>{message || 'Complete checkout below to unlock your booking page.'}</p>
        {error && <p className="helper-text error">{error}</p>}
        <button
          type="button"
          className="auth-submit"
          onClick={onRetry}
          disabled={isRetrying}
        >
          {isRetrying ? 'Refreshing checkout...' : 'Reload checkout'}
        </button>
      </div>
    );
  }

  const coolingDown = resendSecondsLeft > 0;

  return (
    <div className="auth-card activation-notice">
      <h3 ref={headingRef} tabIndex={-1}>
        {emailSent ? 'Check your email' : 'Your signup is saved'}
      </h3>
      {emailSent ? (
        <p>
          We sent a link to <strong>{email}</strong>. Open it to confirm your
          account and continue setup.
        </p>
      ) : (
        <p>
          We couldn&apos;t send the email to <strong>{email}</strong>. Send it
          again, or use a different email.
        </p>
      )}
      {/* The site's .btn classes, not .auth-submit: the public shell's
          `.site button` rule outranks .auth-submit's text colour. */}
      <button
        type="button"
        className={`btn ${emailSent ? 'btn-secondary' : 'btn-primary'} btn-md btn-block`}
        onClick={onResend}
        disabled={resend.isSending || coolingDown}
      >
        {resend.isSending
          ? 'Sending…'
          : coolingDown
            ? `Send it again in ${resendSecondsLeft}s`
            : 'Send it again'}
      </button>
      {resend.message && (
        <p className="helper-text success" role="status">{resend.message}</p>
      )}
      {resend.error && (
        <p className="helper-text error" role="alert">{resend.error}</p>
      )}
      <p className="form-footer-note">
        Wrong address?{' '}
        <button
          type="button"
          className="auth-link"
          onClick={onUseDifferentEmail}
          disabled={resend.isSending}
        >
          Use a different email
        </button>
      </p>
    </div>
  );
};

const EmbeddedCheckoutCard = ({ clientSecret, publishableKey }) => {
  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey]
  );

  if (!clientSecret || !stripePromise) {
    return null;
  }

  return (
    <section className="auth-card embedded-checkout-card">
      <h3>Secure checkout</h3>
      <p className="helper-text">
        Enter your payment details below to activate your plan.
      </p>
      <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </section>
  );
};

const INACTIVE_REFERRAL_MESSAGE =
  'This code is inactive. Please enter a new one or remove it.';

const formatFreeMonths = (months) =>
  `${months} free ${months === 1 ? 'month' : 'months'}`;

const getReferralFeedbackState = (referralFeedback, fallbackCode = '') => {
  const isInactive =
    referralFeedback?.status === 'inactive' || referralFeedback?.active === false;
  const error = isInactive ? INACTIVE_REFERRAL_MESSAGE : referralFeedback?.error || '';

  return {
    code: referralFeedback?.code || fallbackCode,
    isValid: Boolean(referralFeedback?.isValid) && !isInactive,
    error,
    type: referralFeedback?.type || '',
    signupRole: referralFeedback?.signupRole || '',
    perks: referralFeedback?.perks || null,
  };
};

const Signup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const nextPath = useMemo(
    () => new URLSearchParams(location.search || '').get('next') || '',
    [location.search]
  );
  const businessInviteToken = useMemo(
    () => getBusinessInviteTokenFromNext(nextPath),
    [nextPath]
  );
  const invitationNextPath = useMemo(
    () => businessInviteToken
      ? `/join/business?token=${encodeURIComponent(businessInviteToken)}`
      : '',
    [businessInviteToken]
  );
  // Falls back to ?mode= when sessionStorage is blocked, so a privacy-restricted
  // browser can still reach this form instead of being bounced to /signup.
  const initialAccountMode = getSignupMode(location.search);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    industryCategory: '',
    industryCustom: '',
    referralSource: '',
    isProvider: true,
    referralCode: '',
    acceptedTerms: false,
    accountMode: initialAccountMode || '',
  });
  const [businessInvitation, setBusinessInvitation] = useState(null);
  const [invitationError, setInvitationError] = useState('');
  const [pendingSignup, setPendingSignup] = useState(null);
  const [isLaunchingCheckout, setIsLaunchingCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [embeddedClientSecret, setEmbeddedClientSecret] = useState('');
  const [checkoutPublishableKey, setCheckoutPublishableKey] = useState('');
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [isSubscriptionPriceLoading, setIsSubscriptionPriceLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [pendingSignupRecovery, setPendingSignupRecovery] = useState({
    email: '',
    isSending: false,
    error: '',
    message: '',
  });
  const [resendCooldownEndsAt, setResendCooldownEndsAt] = useState(0);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  const emailInputRef = useRef(null);
  const focusEmailOnFormRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referralCodeStatus, setReferralCodeStatus] = useState({
    code: '',
    isValid: false,
    error: '',
    type: '',
    signupRole: '',
  });
  const [founderPerksApplied, setFounderPerksApplied] = useState(false);
  const [recruiterReferralApplied, setRecruiterReferralApplied] = useState(false);
  const promoStatus = {
    isLoading: false,
    error: '',
    remaining: null,
    limit: null,
  };
  const { setCurrentView } = useView();
  const passwordsMismatch =
    formData.confirmPassword &&
    formData.password !== formData.confirmPassword;
  const passwordHasLetterAndNumber =
    /[A-Za-z]/.test(formData.password) && /\d/.test(formData.password);
  const passwordRequirementsInvalid =
    Boolean(formData.password) && !passwordHasLetterAndNumber;
  const isCheckoutActive = Boolean(embeddedClientSecret);
  const showSignupForm = !pendingSignup && !isCheckoutActive;
  const referralForcesProfessionalFlow =
    referralCodeStatus.isValid && referralCodeStatus.signupRole === 'professional';
  const isBusinessInviteSignup = Boolean(businessInviteToken);
  const shouldShowProviderFlow =
    !isBusinessInviteSignup &&
    (formData.isProvider || referralForcesProfessionalFlow) && !recruiterReferralApplied;
  const isOtherIndustrySelected = formData.industryCategory === OTHER_INDUSTRY_CATEGORY;
  const founderMessaging =
    referralCodeStatus.type === 'founding_stylist' || founderPerksApplied;
  const isTrialCoupon =
    referralCodeStatus.isValid &&
    referralCodeStatus.type === 'professional_trial_coupon';
  // The server resolves a coupon's length (default 3) into perks.trialMonths.
  // If that ever arrives malformed the copy falls back to the standard month,
  // which is still true — it just under-promises.
  const trialCouponMonths = isTrialCoupon
    ? Number(referralCodeStatus.perks?.trialMonths) || null
    : null;
  // Months the payment card promises for free. Founding perks carry their own
  // banner, so they pass null and the card stays quiet about the trial.
  const freeTrialMonths = founderPerksApplied ? null : trialCouponMonths || 1;
  // const promoStatusMessage = useMemo(() => {
  //   if (promoStatus.isLoading) return 'Checking availability…';
  //   if (promoStatus.error) return promoStatus.error;
  //   if (promoStatus.remaining === 0) return 'All founding perk spots have been claimed.';
  //   if (promoStatus.limit != null && promoStatus.remaining != null) {
  //     return `${promoStatus.remaining} of ${promoStatus.limit} founding perk spots are still open.`;
  //   }
  //   return 'Founding perk availability is currently limited.';
  // }, [promoStatus]);
  // const promoStatusTone = promoStatus.remaining === 0 ? 'danger' : 'info';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'industryCategory') {
      setFormData((prev) => ({
        ...prev,
        industryCategory: value,
        industryCustom: value === OTHER_INDUSTRY_CATEGORY ? prev.industryCustom : '',
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (name === 'referralCode') {
      setReferralCodeStatus({
        code: value,
        isValid: false,
        error: '',
        type: '',
        signupRole: '',
      });
      setFounderPerksApplied(false);
      setRecruiterReferralApplied(false);
    }
  };

  useEffect(() => {
    if (!isSignupAccountMode(formData.accountMode)) {
      setSubscriptionPlan(null);
      return undefined;
    }

    let isActive = true;
    setIsSubscriptionPriceLoading(true);
    setSubscriptionPlan(null);

    axios.get('/api/payment/subscription/plan-summary', {
      params: { accountMode: formData.accountMode },
    }).then((response) => {
      if (isActive) setSubscriptionPlan(response.data || null);
    }).catch((error) => {
      if (isActive) {
        console.error('Unable to load subscription pricing:', error);
        setSubscriptionPlan(null);
      }
    }).finally(() => {
      if (isActive) setIsSubscriptionPriceLoading(false);
    });

    return () => {
      isActive = false;
    };
  }, [formData.accountMode]);

  // Counted from a timestamp rather than tick by tick, so a tab left in the
  // background while the visitor checks their inbox still unlocks on time.
  useEffect(() => {
    if (!resendCooldownEndsAt) return undefined;
    const timer = setInterval(() => {
      const secondsLeft = Math.max(Math.ceil((resendCooldownEndsAt - Date.now()) / 1000), 0);
      setResendSecondsLeft(secondsLeft);
      if (!secondsLeft) setResendCooldownEndsAt(0);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldownEndsAt]);

  useEffect(() => {
    if (!showSignupForm || !focusEmailOnFormRef.current) return;
    focusEmailOnFormRef.current = false;
    emailInputRef.current?.focus();
  }, [showSignupForm]);

  useEffect(() => {
    if (location.state?.signupError) {
      setFormError({ type: 'generic', message: location.state.signupError });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    if (isBusinessInviteSignup || isSignupAccountMode(formData.accountMode)) return;
    // A valid ?mode= means AccountModeSelection already forwarded us here, so
    // bouncing back would only bounce forward again — an infinite replace loop
    // whenever sessionStorage is unavailable.
    if (getSignupModeFromSearch(location.search)) return;
    navigate(`/signup${location.search || ''}`, { replace: true });
  }, [formData.accountMode, isBusinessInviteSignup, location.search, navigate]);

  useEffect(() => {
    if (!businessInviteToken) {
      setBusinessInvitation(null);
      setInvitationError('');
      return undefined;
    }

    let active = true;
    setInvitationError('');
    axios.post('/api/team/invites/preview', { token: businessInviteToken })
      .then((response) => {
        if (!active) return;
        const invitation = response.data?.invitation || null;
        setBusinessInvitation(invitation);
        if (invitation?.email) {
          setFormData((prev) => ({
            ...prev,
            email: invitation.email,
            isProvider: false,
            accountMode: '',
          }));
        }
      })
      .catch((error) => {
        if (!active) return;
        setInvitationError(
          error.response?.data?.error || 'Unable to load this business invitation.'
        );
      });

    return () => { active = false; };
  }, [businessInviteToken]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const queryCode = (params.get('ref') || params.get('invite') || '').trim();
    if (!queryCode) return;

    setFormData((prev) =>
      prev.referralCode ? prev : { ...prev, referralCode: queryCode }
    );
  }, [location.search]);

  useEffect(() => {
    // eslint-disable-next-line
    let isMounted = true;

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const trimmedCode = formData.referralCode.trim();
    const trimmedEmail = formData.email.trim();
    if (!trimmedCode) {
      setReferralCodeStatus({
        code: '',
        isValid: false,
        error: '',
        type: '',
        signupRole: '',
      });
      setFounderPerksApplied(false);
      setRecruiterReferralApplied(false);
      return undefined;
    }

    let isActive = true;
    const timeoutId = setTimeout(async () => {
      try {
        const shouldValidateRecruiter = Boolean(trimmedEmail);
        const endpoint = shouldValidateRecruiter
          ? '/api/auth/signup/validate-recruiter-referral'
          : '/api/auth/signup/validate-referral';
        // Send the mode the form is in so the live feedback matches what the
        // signup route will enforce on submit.
        const accountMode = formData.accountMode || undefined;
        const payload = shouldValidateRecruiter
          ? { code: trimmedCode, email: trimmedEmail, accountMode }
          : { code: trimmedCode, accountMode };
        const response = await axios.post(endpoint, payload);
        const referralPayload = response.data?.referral || response.data || {};
        if (!isActive) return;
        const referralState = getReferralFeedbackState(referralPayload, trimmedCode);
        setReferralCodeStatus(referralState);
        const recruiterApplied = Boolean(
          referralState.isValid && referralState.signupRole === 'recruiter'
        );
        const perksApplied = Boolean(
          referralState.isValid && referralState.type === 'founding_stylist'
        );
        setRecruiterReferralApplied(recruiterApplied);
        setFounderPerksApplied(perksApplied);
      } catch (error) {
        if (!isActive) return;
        const friendlyMessage = error.response?.data?.error || error.response?.data?.message;
        setReferralCodeStatus({
          code: trimmedCode,
          isValid: false,
          error: friendlyMessage || 'Unable to validate referral code.',
          type: '',
          signupRole: '',
        });
        setFounderPerksApplied(false);
        setRecruiterReferralApplied(false);
      }
    }, 500);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [formData.referralCode, formData.email, formData.accountMode]);

  // Shared with the Stripe success callback so both paths finalize the account consistently.
  const finalizeSignup = (token, user = null) => {
    const accountMode = user?.activeBusiness?.accountMode;
    const redirectPath = invitationNextPath || (accountMode === 'team' ? '/onboarding/team' : '/onboarding');
    clearStoredSignupMode();
    handleAuthSuccess({ token, navigate, setCurrentView, redirectPath, user });
  };

  const launchCheckout = async (pendingSignupId) => {
    try {
      setIsLaunchingCheckout(true);
      setCheckoutError('');
      setEmbeddedClientSecret('');
      const successUrl = `${window.location.origin}/signup/success`;
      const cancelUrl = `${window.location.origin}/signup/cancel`;

      const response = await axios.post('/api/payment/create-checkout-session', {
        pendingSignupId,
        successUrl,
        cancelUrl,
      });

      const { embeddedClientSecret: clientSecret, publishableKey } = response.data || {};
      if (!clientSecret || !publishableKey) {
        throw new Error('Stripe checkout configuration was not returned.');
      }

      setEmbeddedClientSecret(clientSecret);
      setCheckoutPublishableKey(publishableKey || '');
    } catch (error) {
      console.error('Error launching checkout:', error);
      const friendlyMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message;
      setCheckoutError(friendlyMessage || 'Unable to start checkout. Please try again.');
    } finally {
      setIsLaunchingCheckout(false);
    }
  };

  const startResendCooldown = () => {
    setResendCooldownEndsAt(Date.now() + RESEND_COOLDOWN_MS);
    setResendSecondsLeft(RESEND_COOLDOWN_MS / 1000);
  };

  const clearResendCooldown = () => {
    setResendCooldownEndsAt(0);
    setResendSecondsLeft(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.acceptedTerms) {
      setFormError({
        type: 'generic',
        message: 'You must agree to the terms and conditions before continuing.',
      });
      return;
    }
    if (!isBusinessInviteSignup && !formData.referralSource) {
      setFormError({
        type: 'generic',
        message: 'Please tell us how you heard about Haastia.',
      });
      return;
    }
    if (!isBusinessInviteSignup && !formData.industryCategory) {
      setFormError({
        type: 'generic',
        message: 'Please select your industry.',
      });
      return;
    }
    if (!isBusinessInviteSignup && isOtherIndustrySelected && !formData.industryCustom.trim()) {
      setFormError({
        type: 'generic',
        message: 'Please enter your custom industry.',
      });
      return;
    }
    if (passwordsMismatch) {
      setFormError({ type: 'generic', message: 'Passwords must match before continuing.' });
      return;
    }
    if (!passwordHasLetterAndNumber) {
      setFormError({
        type: 'generic',
        message: 'Password must include at least one letter and one number.',
      });
      return;
    }
    try {
      setIsSubmitting(true);
      setCheckoutError('');
      setFormError(null);
      setReferralCodeStatus((prev) => ({
        ...prev,
        error: '',
      }));
      setPendingSignupRecovery({ email: '', isSending: false, error: '', message: '' });
      clearResendCooldown();
      const normalizedIndustryCustom = formData.industryCustom.trim();
      const normalizedIndustry =
        formData.industryCategory === OTHER_INDUSTRY_CATEGORY
          ? normalizedIndustryCustom
          : formData.industryCategory;
      const { confirmPassword, ...signupPayload } = formData;
      const basePayload = {
        ...signupPayload,
        // Guarded by the acceptedTerms check at the top of this handler; the
        // server records these three fields as the consent for this account.
        acceptedTerms: true,
        termsVersion: TERMS_VERSION,
        privacyVersion: PRIVACY_VERSION,
        industryCategory: formData.industryCategory,
        industryCustom:
          formData.industryCategory === OTHER_INDUSTRY_CATEGORY
            ? normalizedIndustryCustom
            : '',
        industry: normalizedIndustry,
        referralCode: formData.referralCode?.trim() || null,
        // Used as the new business's time zone; the server falls back to
        // Eastern when this is missing or unsupported.
        timeZone: getSupportedBrowserTimeZone(),
        ...(isBusinessInviteSignup
          ? {
            businessInviteToken,
            isProvider: false,
            role: 'customer',
            accountMode: undefined,
            industry: '',
            industryCategory: '',
            industryCustom: '',
            referralCode: null,
            heardAbout: null,
          }
          : {}),
      };

      const response = await axios.post('/api/auth/signup', basePayload);
      const {
        token,
        pendingSignupId,
        message,
        foundingStylist,
        referral,
        user,
        emailSent,
      } = response.data || {};
      const foundingActive = Boolean(foundingStylist?.active);
      const foundingTrialEndsAt = foundingStylist?.trialEndsAt || null;
      const foundingTrialDays = foundingStylist?.trialDays ?? null;

      const referralState = getReferralFeedbackState(referral || {}, formData.referralCode || '');
      setReferralCodeStatus(referralState);

      const recruiterApplied = Boolean(
        referralState.isValid && referralState.signupRole === 'recruiter'
      );
      const perksApplied = Boolean(
        foundingActive ||
        (referralState.isValid && referralState.type === 'founding_stylist')
      );
      const professionalTargetedByReferral = Boolean(
        referralState.isValid && referralState.signupRole === 'professional'
      );
      const shouldFollowProviderFlow =
        (formData.isProvider || professionalTargetedByReferral) &&
        !recruiterApplied;
      setRecruiterReferralApplied(recruiterApplied);
      setFounderPerksApplied(perksApplied);

      if (shouldFollowProviderFlow) {
        if (!pendingSignupId) {
          throw new Error('This signup link is invalid or incomplete. Request a new link or restart signup.');
        }

        const signupEmail = formData.email.trim();
        // Only an explicit false means the send failed, so a response without
        // the flag keeps the usual "we sent a link" copy.
        const activationEmailSent = emailSent !== false;
        const pendingState = {
          id: pendingSignupId,
          email: signupEmail,
          emailSent: activationEmailSent,
          message: perksApplied
            ? 'Thanks for joining as a founding stylist. Your 3 free months and concierge onboarding are reserved.'
            : message || 'Thanks for signing up. We sent a secure activation link to your email.',
          foundingStylist: {
            active: foundingActive,
            trialEndsAt: foundingTrialEndsAt,
            trialDays: foundingTrialDays,
          },
        };

        setEmbeddedClientSecret('');
        setPendingSignup(pendingState);
        setPendingSignupRecovery({ email: signupEmail, isSending: false, error: '', message: '' });
        if (activationEmailSent) startResendCooldown();
        localStorage.setItem(
          'haastiaPendingSignupId',
          JSON.stringify({
            id: pendingSignupId,
            status: 'awaiting_email_confirmation',
            foundingStylist: {
              active: foundingActive,
              trialEndsAt: foundingTrialEndsAt,
              trialDays: foundingTrialDays,
            },
            founderPerksApplied: perksApplied,
          })
        );

        return;
      }

      if (!token) throw new Error('No token returned from signup');

      setPendingSignup(null);
      localStorage.removeItem('haastiaPendingSignupId');

      finalizeSignup(token, user);

    } catch (error) {
      console.error('Error signing up:', error);
      const friendlyMessage = error.response?.data?.error || error.response?.data?.message;
      const referralError =
        error.response?.data?.referral?.error;
      if (referralError) {
        setReferralCodeStatus({
          code: formData.referralCode || '',
          isValid: false,
          error: referralError,
          type: '',
          signupRole: '',
        });
      }
      setFounderPerksApplied(false);
      setRecruiterReferralApplied(false);
      if (friendlyMessage === 'Email is already in use') {
        setFormError({
          type: 'existingUser',
          message: 'An account with that email already exists.',
        });
        return;
      }

      if (friendlyMessage === 'A pending signup already exists for this email') {
        setFormError({
          type: 'pendingSignup',
          message: 'You already started signing up with this email.',
        });
        setPendingSignupRecovery((prev) => ({
          ...prev,
          email: formData.email,
          error: '',
          message: '',
        }));
        return;
      }

      setFormError({
        type: 'generic',
        message: friendlyMessage || 'Signup failed. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Back to the form with everything still filled in, ready to fix the email.
  const handleUseDifferentEmail = () => {
    setPendingSignup(null);
    setPendingSignupRecovery({ email: '', isSending: false, error: '', message: '' });
    clearResendCooldown();
    focusEmailOnFormRef.current = true;
  };

  const handlePendingSignupResend = async () => {
    if (
      !pendingSignupRecovery.email ||
      pendingSignupRecovery.isSending ||
      resendSecondsLeft > 0
    ) return;

    try {
      setPendingSignupRecovery((prev) => ({
        ...prev,
        isSending: true,
        error: '',
        message: '',
      }));

      const response = await axios.post('/api/auth/signup/resend-link', {
        email: pendingSignupRecovery.email,
      });

      setPendingSignupRecovery((prev) => ({
        ...prev,
        isSending: false,
        message:
          response.data?.message ||
          'We just sent a new email with your signup link.',
      }));
      setPendingSignup((prev) => (prev ? { ...prev, emailSent: true } : prev));
      startResendCooldown();
    } catch (error) {
      const friendlyMessage = error.response?.data?.error || error.response?.data?.message;
      setPendingSignupRecovery((prev) => ({
        ...prev,
        isSending: false,
        error: friendlyMessage || 'We could not send a new signup link. Please try again.',
      }));
    }
  };

  return (
    <>
      <div className="auth-page auth-page--signup">
        <div className="auth-inner">
          <button
            type="button"
            className="auth-back-button"
            onClick={() => navigate(isBusinessInviteSignup ? invitationNextPath : `/signup${location.search || ''}`)}
          >
            ← Back
          </button>
          <header className="auth-header">
            <span className="eyebrow">
              {isBusinessInviteSignup
                ? 'Business invitation'
                : shouldShowProviderFlow
                  ? 'Create your account · Step 2 of 3'
                  : 'Get started'}
            </span>
            <h1>{isBusinessInviteSignup ? 'Create your account to join' : 'Create your Haastia account'}</h1>
            <p className="sub">
              {isBusinessInviteSignup
                ? 'Use the invited email address. You will review and accept the invitation next.'
                : `Set up your ${formData.accountMode === 'team' ? 'team workspace' : 'account'} so clients can book with you.`}
            </p>
          </header>

          {businessInvitation && (
            <section className="promo-banner" aria-label="Invitation details">
              <div className="promo-badge">Team</div>
              <div className="promo-body">
                <div>
                  <p className="promo-title">{businessInvitation.businessName}</p>
                  <p className="promo-copy">
                    You were invited as {businessInvitation.role === 'business_admin' ? 'a Business Admin' : 'a Team Member'}.
                  </p>
                </div>
              </div>
            </section>
          )}
          {invitationError && <p className="helper-text error" role="alert">{invitationError}</p>}

          {recruiterReferralApplied && (
            <section className="promo-banner" aria-live="polite">
              <div className="promo-badge">Referrer</div>
              <div className="promo-body">
                <div>
                  <p className="promo-title">You’re all set!</p>
                  <p className="promo-copy">
                    Referrer access is free forever. You can start referring providers and earning payouts right away.
                  </p>
                </div>
                <div className="promo-status success">
                  <span className="promo-status-dot" aria-hidden="true" />
                  <span>Referrer access activated.</span>
                </div>
              </div>
            </section>
          )}

          {founderPerksApplied && (
            <section className="promo-banner" aria-live="polite">
              <div className="promo-badge">Founding stylist</div>
              <div className="promo-body">
                <div>
                  <p className="promo-title">Thank you for being a founding stylist.</p>
                  <p className="promo-copy">
                    You&apos;ll receive 3 free months, concierge onboarding, and early product access.
                  </p>
                </div>
                <div className="promo-status success">
                  <span className="promo-status-dot" aria-hidden="true" />
                  <span>Founding stylist perks applied to your account.</span>
                </div>
              </div>
            </section>
          )}

          {showSignupForm && (
            <section className="auth-form">
              <form className="auth-card" onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="signup-name">Full name</label>
                    <div className="input-shell">
                      <input
                        id="signup-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Jane Doe"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="signup-email">Email address</label>
                    <div className="input-shell">
                      <input
                        ref={emailInputRef}
                        id="signup-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        readOnly={isBusinessInviteSignup}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="signup-password">Password</label>
                    <PasswordField
                      id="signup-password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a secure password"
                      autoComplete="new-password"
                      required
                    />
                    {passwordRequirementsInvalid ? (
                      <p className="helper-text error">
                        Password must include at least one letter and one number.
                      </p>
                    ) : (
                      <p className="helper-text">
                        Use at least 8 characters with a mix of letters and numbers.
                      </p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="signup-confirm-password">Confirm password</label>
                    <PasswordField
                      id="signup-confirm-password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Re-enter your password"
                      autoComplete="new-password"
                      required
                    />
                    {passwordsMismatch && (
                      <p className="helper-text error">Passwords must match.</p>
                    )}
                  </div>

                  {!isBusinessInviteSignup && (
                    <>
                  <div className="form-group">
                    <label htmlFor="signup-industry">Industry</label>
                    <div className="input-shell">
                      <select
                        id="signup-industry"
                        name="industryCategory"
                        value={formData.industryCategory}
                        onChange={handleChange}
                        required
                      >
                        <option value="" disabled>
                          Select your industry
                        </option>
                        {INDUSTRY_CATEGORY_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <p className="helper-text">What type of services do you offer?</p>
                    {isOtherIndustrySelected && (
                      <>
                        <label htmlFor="signup-industry-custom">Tell us your industry</label>
                        <div className="input-shell">
                          <input
                            id="signup-industry-custom"
                            type="text"
                            name="industryCustom"
                            value={formData.industryCustom}
                            onChange={handleChange}
                            placeholder="Enter your industry"
                            required
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="signup-referral-source">How did you hear about Haastia?</label>
                    <div className="input-shell">
                      <select
                        id="signup-referral-source"
                        name="referralSource"
                        value={formData.referralSource}
                        onChange={handleChange}
                        required
                      >
                        <option value="" disabled>
                          Select an option
                        </option>
                        <option value="search">Search engine</option>
                        <option value="social">Social media</option>
                        <option value="friend">Friend or colleague</option>
                        <option value="event">Event or conference</option>
                        <option value="ad">Online ad</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <p className="helper-text">This helps us understand how people discover Haastia.</p>
                  </div>

                  <div className="form-group">
                    <label htmlFor="signup-referral">Referral or invite code</label>
                    <div className="input-shell">
                      <input
                        id="signup-referral"
                        type="text"
                        name="referralCode"
                        value={formData.referralCode}
                        onChange={handleChange}
                        placeholder="Enter your referral code"
                        aria-describedby="signup-referral-help-text"
                      />
                    </div>
                    <p
                      id="signup-referral-help-text"
                      className={`helper-text ${referralCodeStatus.error ? 'error' : referralCodeStatus.isValid ? 'success' : ''
                        }`}
                    >
                      {referralCodeStatus.error ||
                        (referralCodeStatus.isValid
                          ? referralCodeStatus.type === 'founding_stylist'
                            ? 'Founding stylist code applied. Your founding stylist account is ready.'
                            : referralCodeStatus.type === 'recruiter_invite'
                              ? 'Recruiter invite accepted. Your recruiter account is ready.'
                              : referralCodeStatus.type === 'professional_trial_coupon'
                                ? `Trial coupon applied: ${formatFreeMonths(trialCouponMonths || 1)}.`
                                : `Referral code applied: ${referralCodeStatus.code || ''}.`
                          : 'Have a referral or invite code? Add it here (optional).')}
                    </p>
                  </div>
                    </>
                  )}

                  <div className="checkbox-card">
                    <input
                      id="signup-accepted-terms"
                      type="checkbox"
                      name="acceptedTerms"
                      checked={formData.acceptedTerms}
                      onChange={handleChange}
                    />
                    <label htmlFor="signup-accepted-terms">
                      I agree to the{' '}
                      <Link className="auth-link" to="/terms" target="_blank" rel="noopener noreferrer">
                        terms and conditions
                      </Link>{' '}
                      and the{' '}
                      <Link className="auth-link" to="/privacy" target="_blank" rel="noopener noreferrer">
                        privacy policy
                      </Link>
                    </label>
                  </div>

                  {shouldShowProviderFlow && (
                    <>
                      <p className="helper-text">
                        {founderMessaging
                          ? 'You are on the founding stylist path. We will confirm your account and kick off your complimentary months after signup.'
                          : ''}
                      </p>
                      <ProfessionalPaymentSection
                        promoStatus={promoStatus}
                        hasFoundingPerkAvailability={founderPerksApplied}
                        freeTrialMonths={freeTrialMonths}
                        accountMode={formData.accountMode}
                        planName={subscriptionPlan?.planName}
                        price={subscriptionPlan?.price}
                        isPriceLoading={isSubscriptionPriceLoading}
                      />
                    </>
                  )}
                </div>

                {formError && (
                  <div className="form-error">
                    <p className="helper-text error">{formError.message}</p>
                    {formError.type === 'existingUser' && (
                      <p className="helper-text">
                        Already have an account?{' '}
                        <Link className="auth-link" to="/login">
                          Log in
                        </Link>
                        .
                      </p>
                    )}
                    {formError.type === 'pendingSignup' && (
                      <div className="helper-text">
                        <p className="helper-text">
                          We can send you a new email with your signup link.
                        </p>
                        <button
                          type="button"
                          className="resend-link-button"
                          onClick={handlePendingSignupResend}
                          disabled={
                            pendingSignupRecovery.isSending ||
                            !pendingSignupRecovery.email ||
                            resendSecondsLeft > 0
                          }
                        >
                          {pendingSignupRecovery.isSending
                            ? 'Sending email…'
                            : resendSecondsLeft > 0
                              ? `Send another link in ${resendSecondsLeft}s`
                              : 'Send me a new signup link'}
                        </button>
                        {pendingSignupRecovery.message && (
                          <p className="helper-text success">{pendingSignupRecovery.message}</p>
                        )}
                        {pendingSignupRecovery.error && (
                          <p className="helper-text error">{pendingSignupRecovery.error}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <button
                  className="auth-submit"
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !formData.acceptedTerms ||
                    (isBusinessInviteSignup && (!businessInvitation || Boolean(invitationError)))
                  }
                >
                  {isSubmitting ? 'Submitting...' : 'Create account'}
                </button>
                <p className="form-footer-note">
                  Already have an account?{' '}
                  <Link className="auth-link" to="/login">
                    Log in
                  </Link>
                  .
                </p>
              </form>
            </section>
          )}

          {pendingSignup && (
            <>
              <ProfessionalActivationNotice
                email={pendingSignup.email}
                emailSent={pendingSignup.emailSent}
                message={pendingSignup.message}
                onRetry={() => launchCheckout(pendingSignup.id)}
                isRetrying={isLaunchingCheckout}
                error={checkoutError}
                awaitingEmailConfirmation={!embeddedClientSecret}
                resend={pendingSignupRecovery}
                resendSecondsLeft={resendSecondsLeft}
                onResend={handlePendingSignupResend}
                onUseDifferentEmail={handleUseDifferentEmail}
              />
              <EmbeddedCheckoutCard
                clientSecret={embeddedClientSecret}
                publishableKey={checkoutPublishableKey}
              />
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Signup;
